/**
 * MainLayout — konsolidierte Plattform-Shell (UX_KONZEPT §1.2/§6.5):
 * EINE dunkle Kopfzeile (TopBar, trägt Burger/AppSwitcher/Wortmarke/Suche/
 * EnvBadge/User/Abmelden) + linke Sidebar + Content, nur `<main>` scrollt.
 * Design-Korrektur ggü. der ursprünglichen rmnc-personal-Vorlage: dort gab
 * es zusätzlich einen hellen App-Header als zweite Kopfzeile (Duplikat von
 * Suche/App-Name/User) — das war ein Fehler und wird dort korrigiert, hier
 * bewusst nicht nachgebaut. `<main>` nutzt volle Breite (`w-full px-6 py-8`,
 * Angleichung an die Technik-Vorlage — kein mx-auto/max-w-Container mehr).
 */
import { Suspense, useEffect, useState } from "react"
import { Outlet } from "react-router-dom"
import { Sidebar } from "./Sidebar"
import { TopBar } from "./TopBar"
import { CommandPalette } from "@/components/CommandPalette"
import { useLinks } from "@/components/AppSwitcher"
import { TooltipProvider } from "@/components/ui/tooltip"
import { LoadingSpinner } from "@/components/shared"

/** Akzent Stammdaten (SHELL_PORTIERUNG.md §4 — #9b59b6). */
const STAMMDATEN_ACCENT = "283 39% 53%"

/** localStorage-Schlüssel für den Einklapp-Zustand der Sidebar. */
const COLLAPSED_STORAGE_KEY = "rmnc-stammdaten.sidebar.collapsed"

export function MainLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1"
    } catch {
      return false
    }
  })
  const [paletteOpen, setPaletteOpen] = useState(false)
  const links = useLinks()

  // Einklapp-Zustand über Reloads hinweg merken.
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, sidebarCollapsed ? "1" : "0")
    } catch {
      /* Privacy-Mode: Zustand bleibt in-memory */
    }
  }, [sidebarCollapsed])

  // Globaler Hotkey ⌘K (Mac) / Strg-K (Win/Linux) — Toggle der Palette.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  return (
    <TooltipProvider>
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        {/* Einzige Kopfzeile: dunkle Plattform-Leiste (Burger/AppSwitcher/
            Wortmarke links, Suche/EnvBadge/User/Abmelden rechts) */}
        <TopBar
          onOpenPalette={() => setPaletteOpen(true)}
          onToggleSidebar={() => setSidebarCollapsed((c) => !c)}
        />

        <div className="flex flex-1 overflow-hidden">
          <Sidebar collapsed={sidebarCollapsed} accent={STAMMDATEN_ACCENT} />

          {/* Volle Breite wie Technik-Vorlage (kein mx-auto/max-w-Container) */}
          <main className="w-full flex-1 overflow-auto px-6 py-8">
            <Suspense fallback={<LoadingSpinner fullPage />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} links={links} />
    </TooltipProvider>
  )
}
